import { IonButton, IonIcon } from "@ionic/react";
import { ComponentProps, ReactNode } from "react";
import { useIntl } from "react-intl";

import styles from "@components/buttons/BoxIconButton.module.css";
import ConditionalTooltip from "@components/ConditionalTooltip";
import useKeyboardFocus from "@hooks/useKeyboardFocus";
import { IconList, IconName } from "@utils/iconUtils";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type LinkProps = Omit<
  Pick<ComponentProps<typeof IonButton>, "disabled" | "href" | "onClick" | "rel" | "routerLink" | "target" | "type">,
  "mode"
>;

type Props = LinkProps & {
  ariaLabelKey: string;
  hoverContent?: ReactNode;
  icon: IconName;
  size?: "medium" | "large";
  skipSpacing?: boolean;
};

const BoxIconButton = ({
  ariaLabelKey,
  hoverContent,
  icon,
  size = "medium",
  skipSpacing = false,
  ...linkProps
}: Props) => {
  const keyboardFocus = useKeyboardFocus();
  const intl = useIntl();

  return (
    <div className={skipSpacing ? styles.vertical : styles.vertical}>
      <ConditionalTooltip condition={!!hoverContent} content={hoverContent}>
        {tooltipTriggerProps => (
          <IonButton
            className={`${styles.button} ${styles[size]} ${keyboardFocus ? styles.focus : ""}`}
            {...linkProps}
            aria-describedby={tooltipTriggerProps?.["aria-describedby"]}
            mode="md"
            onIonBlur={tooltipTriggerProps?.onBlur}
            onIonFocus={tooltipTriggerProps?.onFocus}
            onKeyUp={clickCurrentTarget}
            role={tooltipTriggerProps?.role}
          >
            <IonIcon
              aria-label={intl.formatMessage({ id: ariaLabelKey })}
              className={styles.icon}
              src={IconList[icon]}
            />
          </IonButton>
        )}
      </ConditionalTooltip>
    </div>
  );
};

export default BoxIconButton;
